import React, { useMemo, useState } from "react";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import PickerTable from "./PickerTable";

/**
 * Products tab body: name/SKU/category filters + editable-qty table.
 * Emits the standard product order-line payload via `onAdd`.
 */
export default function ProductsPanel({ products, branch, onAdd }) {
  const [productQ, setProductQ] = useState("");
  const [sku, setSku] = useState("");
  const [productCat, setProductCat] = useState("all");

  // PRO Product Gating (Feb 2026): retail checkout must strictly hide any
  // product flagged `is_pro` (professional-use only). We derive `retailProducts`
  // once here so both the category dropdown and the search results share the
  // same PRO-free source of truth — otherwise a PRO-only category could still
  // appear in the filter list even though its rows are hidden.
  const retailProducts = useMemo(
    () => products.filter((p) => !p.is_pro),
    [products]
  );

  const productCategories = useMemo(() => {
    const set = new Set();
    for (const p of retailProducts) if (p.category) set.add(p.category);
    return ["all", ...[...set].sort()];
  }, [retailProducts]);

  const filteredProducts = useMemo(() => {
    const q = productQ.trim().toLowerCase();
    const s = sku.trim().toLowerCase();
    return retailProducts.filter((p) =>
      (q === "" || (p.name || "").toLowerCase().includes(q)) &&
      (s === "" || (p.sku || "").toLowerCase().includes(s)) &&
      (productCat === "all" || p.category === productCat)
    );
  }, [retailProducts, productQ, sku, productCat]);

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 sm:grid-cols-[1fr_140px_180px] gap-2">
        <Input placeholder="Filter by product name…" value={productQ} onChange={(e) => setProductQ(e.target.value)} data-testid="add-product-name" />
        <Input placeholder="SKU" value={sku} onChange={(e) => setSku(e.target.value)} data-testid="add-product-sku" />
        <Select value={productCat} onValueChange={setProductCat}>
          <SelectTrigger data-testid="add-product-category"><SelectValue placeholder="Category" /></SelectTrigger>
          <SelectContent className="max-h-72">
            {productCategories.map((c) => (
              <SelectItem key={c} value={c}>{c === "all" ? "All categories" : c}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <PickerTable
        rows={filteredProducts}
        branch={branch}
        onAdd={(row, qty) => onAdd({ kind: "product", ref_id: row.id, name: row.name, quantity: qty, unit_price: row.price || 0, discount: 0 })}
        cols={[
          { header: "Product", cell: (r) => <div className="font-medium text-sm">{r.name}</div> },
          { header: "Category", cell: (r) => <span className="text-xs text-muted-foreground">{r.category || "-"}</span> },
          { header: "SKU", cell: (r) => <span className="text-xs text-muted-foreground">{r.sku || "-"}</span> },
        ]}
        testidPrefix="add-product"
      />
    </div>
  );
}
